import { Fragment, useMemo, useState } from "react";
import {
  CELL_CLASSES,
  INFO_LABEL,
  INFO_STRIP,
  TOTAL_CELL_CLASSES,
  fmt,
  margin,
  marginBand,
  shortName,
} from "./utils";
import { EmptyState } from "../../../components/UI/States";

const TH_BASE =
  "sticky top-0 z-[2] cursor-pointer select-none whitespace-nowrap border-r border-[#444] bg-[#5a5a5a] px-2 py-[7px] text-white hover:bg-[#e8a23a] dark:bg-slate-700 dark:hover:bg-amber-600";

// Column index scheme (matches the original): 0=Customer, 1=Type,
// 2=Total Sales, 3=Total Margin, then Sales/Margin pairs per PG1.
const sortValueFor = (cust, colIdx, pg1List) => {
  if (colIdx === 0) return cust.name;
  if (colIdx === 1) return cust.type;
  if (colIdx === 2) return cust.totalSales;
  if (colIdx === 3) return margin(cust.totalSales, cust.totalCost);
  const offset = colIdx - 4;
  const pg1 = pg1List[Math.floor(offset / 2)];
  if (!pg1) return null;
  const p = cust.pg1s[pg1.id];
  if (!p) return null;
  return offset % 2 === 1 ? margin(p.sales, p.cost) : p.sales;
};

const SummaryTab = ({ pg1List, custList, overallCo, info }) => {
  const [sort, setSort] = useState({ col: null, dir: "desc" });

  const toggleSort = (col) =>
    setSort((prev) =>
      prev.col === col
        ? { col, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { col, dir: "desc" }
    );

  const sorted = useMemo(() => {
    if (sort.col === null) return custList;
    const stringCol = sort.col <= 1;
    return [...custList].sort((a, b) => {
      let av = sortValueFor(a, sort.col, pg1List);
      let bv = sortValueFor(b, sort.col, pg1List);
      if (stringCol) {
        const cmp = String(av || "").localeCompare(String(bv || ""));
        return sort.dir === "asc" ? cmp : -cmp;
      }
      if (av == null) av = -Infinity;
      if (bv == null) bv = -Infinity;
      return sort.dir === "asc" ? av - bv : bv - av;
    });
  }, [custList, pg1List, sort]);

  if (!custList.length) {
    return (
      <EmptyState compact title="No data returned for the selected parameters." />
    );
  }

  const grandSales = custList.reduce((s, c) => s + c.totalSales, 0);
  const grandCost = custList.reduce((s, c) => s + c.totalCost, 0);
  const grandMargin = margin(grandSales, grandCost);
  const grandBand = marginBand(grandMargin, overallCo, grandSales > 0);

  const arrow = (col) =>
    sort.col === col ? (sort.dir === "asc" ? " ▲" : " ▼") : "";

  return (
    <div>
      <div className={INFO_STRIP}>
        <span>
          <strong className={INFO_LABEL}>Rep:</strong> {info.repName}
        </span>
        <span>
          <strong className={INFO_LABEL}>Period:</strong> {info.fromDate} → {info.toDate}
        </span>
        <span>
          <strong className={INFO_LABEL}>Company Margin:</strong> {fmt.pct(overallCo)}
        </span>
        <span>
          <strong className={INFO_LABEL}>Customers:</strong> {custList.length}
        </span>
        <span>
          <strong className={INFO_LABEL}>Total Sales:</strong> {fmt.dollars(grandSales)}
        </span>
      </div>

      <div className="app-divider overflow-x-auto rounded-xl border">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr>
              <th
                colSpan={4}
                className="sticky top-0 z-[2] bg-[#3d3d3d] px-2 py-[5px] dark:bg-slate-800"
              />
              {pg1List.map((pg1) => (
                <th
                  key={pg1.id}
                  colSpan={2}
                  className="sticky top-0 z-[2] whitespace-nowrap bg-[#6a5a3a] px-2 py-[5px] text-center text-[11px] font-bold tracking-wide text-[#ffe5b0] dark:bg-amber-900/80"
                >
                  {shortName(pg1.name)}
                </th>
              ))}
            </tr>
            <tr>
              {["Customer", "Type", "Total Sales", "Total Margin"].map(
                (label, i) => (
                  <th
                    key={label}
                    onClick={() => toggleSort(i)}
                    className={`${TH_BASE} ${i === 0 ? "text-left" : "text-right"}`}
                  >
                    {label}
                    {arrow(i)}
                  </th>
                )
              )}
              {pg1List.map((pg1, i) => (
                <Fragment key={pg1.id}>
                  <th
                    onClick={() => toggleSort(4 + i * 2)}
                    className={`${TH_BASE} text-right`}
                  >
                    Sales{arrow(4 + i * 2)}
                  </th>
                  <th
                    onClick={() => toggleSort(4 + i * 2 + 1)}
                    className={`${TH_BASE} text-right`}
                  >
                    Margin{arrow(4 + i * 2 + 1)}
                  </th>
                </Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((cust) => {
              const custMargin = margin(cust.totalSales, cust.totalCost);
              const custBand = marginBand(
                custMargin,
                overallCo,
                cust.totalSales > 0
              );
              return (
                <tr
                  key={cust.id}
                  className="group even:bg-[color:var(--app-hover)]"
                >
                  <td className="min-w-[180px] max-w-[260px] border-b border-r border-[color:var(--app-divider)] px-2 py-[5px] text-left font-semibold group-hover:bg-amber-500/10">
                    {cust.name}
                    <span className="app-muted block text-[11px] font-normal">
                      {cust.type}
                    </span>
                  </td>
                  <td className="app-muted whitespace-nowrap border-b border-r border-[color:var(--app-divider)] px-2 py-[5px] text-right group-hover:bg-amber-500/10">
                    {cust.type}
                  </td>
                  <td className="whitespace-nowrap border-b border-r border-[color:var(--app-divider)] px-2 py-[5px] text-right tabular-nums group-hover:bg-amber-500/10">
                    {fmt.dollars(cust.totalSales)}
                  </td>
                  <td
                    className={`whitespace-nowrap border-b border-r border-[color:var(--app-divider)] px-2 py-[5px] text-right tabular-nums ${CELL_CLASSES[custBand]}`}
                  >
                    {custMargin != null ? fmt.pct(custMargin) : "—"}
                  </td>
                  {pg1List.map((pg1) => {
                    const p = cust.pg1s[pg1.id];
                    if (p && p.sales > 0) {
                      const m = margin(p.sales, p.cost);
                      const band = marginBand(m, pg1.companyMargin, true);
                      return (
                        <Fragment key={pg1.id}>
                          <td className="whitespace-nowrap border-b border-r border-[color:var(--app-divider)] px-2 py-[5px] text-right tabular-nums group-hover:bg-amber-500/10">
                            {fmt.dollars(p.sales)}
                          </td>
                          <td
                            className={`whitespace-nowrap border-b border-r border-[color:var(--app-divider)] px-2 py-[5px] text-right tabular-nums ${CELL_CLASSES[band]}`}
                          >
                            {m != null ? fmt.pct(m) : "—"}
                          </td>
                        </Fragment>
                      );
                    }
                    return (
                      <Fragment key={pg1.id}>
                        <td
                          className={`whitespace-nowrap border-b border-r border-[color:var(--app-divider)] px-2 py-[5px] text-right tabular-nums ${CELL_CLASSES.noSales}`}
                        >
                          —
                        </td>
                        <td
                          className={`whitespace-nowrap border-b border-r border-[color:var(--app-divider)] px-2 py-[5px] text-right tabular-nums ${CELL_CLASSES.noSales}`}
                        >
                          —
                        </td>
                      </Fragment>
                    );
                  })}
                </tr>
              );
            })}

            <tr className="bg-[#3d3d3d] font-bold text-white dark:bg-slate-800">
              <td
                colSpan={2}
                className="border-b-2 border-[#e8a23a] px-2 py-[5px] text-left"
              >
                TOTAL ({sorted.length} customers)
              </td>
              <td className="whitespace-nowrap border-b-2 border-[#e8a23a] px-2 py-[5px] text-right tabular-nums">
                {fmt.dollars(grandSales)}
              </td>
              <td
                className={`whitespace-nowrap border-b-2 border-[#e8a23a] px-2 py-[5px] text-right tabular-nums ${TOTAL_CELL_CLASSES[grandBand]}`}
              >
                {grandMargin != null ? fmt.pct(grandMargin) : "—"}
              </td>
              {pg1List.map((pg1) => {
                const pgSales = custList.reduce(
                  (s, c) => s + ((c.pg1s[pg1.id] && c.pg1s[pg1.id].sales) || 0),
                  0
                );
                const pgCost = custList.reduce(
                  (s, c) => s + ((c.pg1s[pg1.id] && c.pg1s[pg1.id].cost) || 0),
                  0
                );
                const pgM = margin(pgSales, pgCost);
                const band = marginBand(pgM, pg1.companyMargin, pgSales > 0);
                return (
                  <Fragment key={pg1.id}>
                    <td className="whitespace-nowrap border-b-2 border-[#e8a23a] px-2 py-[5px] text-right tabular-nums">
                      {fmt.dollars(pgSales)}
                    </td>
                    <td
                      className={`whitespace-nowrap border-b-2 border-[#e8a23a] px-2 py-[5px] text-right tabular-nums ${TOTAL_CELL_CLASSES[band]}`}
                    >
                      {pgM != null ? fmt.pct(pgM) : "—"}
                    </td>
                  </Fragment>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SummaryTab;
